import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { fileUrl, api } from "../lib/apiClient";
import { Loader2, X, Sparkles, RotateCcw, Heart, Wand2, Pin, PinOff, Share2, Copy, Check, Download } from "lucide-react";
import { toast } from "sonner";

export default function StyleBoard({ open, onOpenChange, onTasteChange }) {
  const [loading, setLoading] = useState(false);
  const [picks, setPicks] = useState([]);
  const [terms, setTerms] = useState([]);
  const [count, setCount] = useState(0);
  const [nudge, setNudge] = useState("");
  const [nudging, setNudging] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/ai/style-board")
      .then((r) => { setPicks(r.data.picks || []); setTerms(r.data.terms || []); setCount(r.data.count || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const removeTerm = async (t) => {
    setTerms((arr) => arr.filter((x) => x.term !== t));
    try {
      const { data } = await api.post("/ai/taste-profile/edit", { remove: [t] });
      setTerms(data.terms || []);
      onTasteChange?.(data.top_terms || []);
    } catch { toast.error("Could not update taste"); load(); }
  };

  const reset = async () => {
    try {
      await api.post("/ai/taste-profile/edit", { reset: true });
      setTerms([]);
      onTasteChange?.([]);
      toast.success("Learned taste reset");
    } catch { toast.error("Could not reset"); }
  };

  const applyNudge = async () => {
    const text = nudge.trim();
    if (!text) return;
    setNudging(true);
    try {
      const { data } = await api.post("/ai/taste-profile/nudge", { text });
      setTerms(data.terms || []);
      onTasteChange?.(data.top_terms || []);
      setNudge("");
      const bits = [];
      if (data.added?.length) bits.push(`+ ${data.added.join(", ")}`);
      if (data.removed?.length) bits.push(`− ${data.removed.join(", ")}`);
      toast.success(bits.length ? `Steered ${bits.join("  ")}` : "Taste updated");
    } catch { toast.error("Could not apply nudge"); }
    finally { setNudging(false); }
  };

  const togglePin = async (p) => {
    const next = !p.pinned;
    setPicks((arr) => arr.map((x) => (x.redesign_id === p.redesign_id ? { ...x, pinned: next } : x)));
    try {
      await api.post(`/ai/redesigns/${p.redesign_id}/pin`, { pinned: next });
      toast.success(next ? "Pinned to board — taste updated" : "Unpinned");
      load();
    } catch { toast.error("Could not update pin"); load(); }
  };

  const shareBoard = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/ai/style-board/share", {});
      const url = `${window.location.origin}/board/${data.share_id}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
      toast.success("Client link ready — copied to clipboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create share link");
    } finally { setSharing(false); }
  };

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Copied"); } catch {}
  };

  const [pdfing, setPdfing] = useState(false);
  const downloadPdf = async () => {
    setPdfing(true);
    try {
      const { data } = await api.post("/ai/style-board/pdf", {});
      const res = await api.get(`/files/${data.path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "style_direction_board.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Board PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build PDF");
    } finally { setPdfing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0D1117] border-[#21262D] text-[#F0F6FC] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="style-board-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <Heart className="w-5 h-5 text-[#F59E0B]" /> Your Style Memory Board
          </DialogTitle>
          <p className="text-[#8B949E] text-sm">Designs you've pinned and the taste the agent has learned. Steer it in words or remove any term.</p>
        </DialogHeader>

        <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-[#8B949E]"><Share2 className="w-4 h-4 text-[#F59E0B]" /> Share this direction with your client to confirm before rendering</div>
            <Button onClick={shareBoard} disabled={sharing} className="text-[#0D1117] font-semibold bg-[#F59E0B] hover:bg-[#d97706] shrink-0" data-testid="board-share-btn">
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4 mr-1" /> Share with client</>}
            </Button>
          </div>
          {shareUrl && (
            <div className="flex items-center gap-2 mt-2" data-testid="board-share-link">
              <Input readOnly value={shareUrl} className="bg-[#0D1117] border-[#30363D] text-xs" />
              <Button onClick={copyUrl} variant="outline" className="border-[#30363D] shrink-0" data-testid="board-copy-btn">
                {copied ? <Check className="w-4 h-4 text-[#34D399]" /> : <Copy className="w-4 h-4" />}
              </Button>
              <a href={shareUrl} target="_blank" rel="noreferrer" className="text-xs text-[#38BDF8] hover:underline shrink-0 whitespace-nowrap" data-testid="board-open-link">Open ↗</a>
            </div>
          )}
          <div className="mt-2">
            <Button onClick={downloadPdf} disabled={pdfing} variant="outline" className="w-full border-[#F59E0B]/40 text-[#F59E0B] hover:border-[#F59E0B] hover:text-[#F59E0B]" data-testid="board-pdf-btn">
              {pdfing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Building PDF…</> : <><Download className="w-4 h-4 mr-1" /> Download as PDF (email attachment)</>}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-[#8B949E]"><Loader2 className="w-5 h-5 animate-spin text-[#F59E0B]" /> Loading your board…</div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border border-[#38BDF8]/30 bg-[#38BDF8]/5 p-3">
              <label className="text-xs uppercase tracking-widest text-[#38BDF8] flex items-center gap-1.5 mb-2"><Wand2 className="w-3.5 h-3.5" /> Steer the style in words</label>
              <div className="flex gap-2">
                <Input value={nudge} onChange={(e) => setNudge(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") applyNudge(); }}
                  placeholder="e.g. less walnut, more colour and brass"
                  className="bg-[#0D1117] border-[#30363D] text-sm" data-testid="taste-nudge-input" />
                <Button onClick={applyNudge} disabled={nudging || !nudge.trim()} className="text-[#0D1117] font-semibold bg-[#38BDF8] hover:bg-[#0ea5e9] shrink-0" data-testid="taste-nudge-btn">
                  {nudging ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-widest text-[#8B949E] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#F59E0B]" /> Learned taste ({count} picks)</span>
                {terms.length > 0 && (
                  <button onClick={reset} className="text-[11px] text-[#8B949E] hover:text-[#EF4444] flex items-center gap-1" data-testid="taste-reset-btn"><RotateCcw className="w-3 h-3" /> Reset</button>
                )}
              </div>
              {terms.length === 0 ? (
                <p className="text-xs text-[#6E7681]">No taste yet — pick favourites or steer it in words above.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="board-terms">
                  {terms.map((t) => (
                    <span key={t.term} className="group text-xs px-2 py-1 rounded-full bg-[#161B22] border border-[#30363D] text-[#F59E0B] flex items-center gap-1" data-testid={`board-term-${t.term.replace(/[^\w]+/g, "-")}`}>
                      {t.term}
                      <button onClick={() => removeTerm(t.term)} className="text-[#6E7681] hover:text-[#EF4444]" data-testid="board-term-remove"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <span className="text-xs uppercase tracking-widest text-[#8B949E]">Board designs</span>
              {picks.length === 0 ? (
                <p className="text-xs text-[#6E7681] mt-2">Nothing here yet. Pick or pin a design and it shows up here.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2" data-testid="board-picks">
                  {picks.map((p) => (
                    <div key={p.redesign_id} className="rounded-lg overflow-hidden border border-[#21262D] bg-[#161B22]" data-testid={`board-pick-${p.redesign_id}`}>
                      <div className="relative group">
                        <img src={fileUrl(p.image)} alt={p.name || p.space} className="w-full h-24 object-cover" />
                        {p.bold && <span className="absolute top-1 left-1 text-[8px] px-1 py-0.5 rounded bg-[#F59E0B] text-[#0D1117] font-bold">BOLD</span>}
                        <button onClick={() => togglePin(p)} title={p.pinned ? "Unpin" : "Pin"}
                          className={`absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center ${p.pinned ? "bg-[#F59E0B] text-[#0D1117]" : "bg-black/60 text-white hover:bg-black/80"}`}
                          data-testid={`board-pin-${p.redesign_id}`}>
                          {p.pinned ? <Pin className="w-3 h-3" /> : <PinOff className="w-3 h-3" />}
                        </button>
                      </div>
                      <div className="p-1.5">
                        <div className="text-[11px] font-semibold truncate">{p.name || p.space}</div>
                        {p.palette && <div className="text-[9px] text-[#8B949E] truncate">{p.palette}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
